import React from 'react';

import { useTranslation } from '@lt/localization';
import { Button } from '@lt/components';
import { ReviewsCardSkeleton } from '@lt/components/icons-react/skeletons';
import LoaderIcon from '@lt/components/icons-react/rebrand/Loader';

import ReviewCard from 'src/components/Reviews/ReviewCard/ReviewCard';
import NotEnoughReviews from 'src/components/Reviews/components/NotEnoughReviews';

import type { ReviewsProps } from '@lt/api/dist/methods/hotelReviews/types';
import ErrorBanner from 'src/components/Reviews/components/ErrorBanner';
import styles from './reviewsList.module.css';
import {
  REVIEWS_LIST_SKELETON_TEST_ID,
  REVIEWS_LIST_TEST_ID,
} from './constants';

export interface ReviewListProps {
  items: ReviewsProps[];
  showMore: () => void;
  isShowMoreReviews: boolean;
  isLoadingNew: boolean;
  hasReviewsSection: boolean;
  isFetching: boolean;
  selectedFilters?: string[];
  isErrorResult: boolean;
  onClickRetry: () => void;
}

export const ReviewList = ({
  items,
  isShowMoreReviews,
  showMore,
  isLoadingNew,
  hasReviewsSection,
  isFetching,
  selectedFilters = [],
  isErrorResult,
  onClickRetry,
}: ReviewListProps): JSX.Element | null => {
  const { t } = useTranslation();
  if (isErrorResult) return <ErrorBanner onClickRetry={onClickRetry} />;
  if (isFetching)
    return (
      <div data-testid={REVIEWS_LIST_SKELETON_TEST_ID}>
        <ReviewsCardSkeleton className={styles.skeleton} />
        <ReviewsCardSkeleton className={styles.skeleton} />
        <ReviewsCardSkeleton className={styles.skeleton} />
      </div>
    );
  if (!hasReviewsSection) return <NotEnoughReviews isEmptyReviews />;

  const handleClick = () => {
    showMore();
  };

  return (
    <>
      <div className={styles.reviewsList} data-testid={REVIEWS_LIST_TEST_ID}>
        {items.map((item) => (
          <ReviewCard
            className={styles.reviewsCard}
            key={item.id}
            id={item.id}
            content={item.content}
            name={item.name}
            createdAt={item.createdAt}
            averageRating={item.averageRating}
            avatarUrl={item.avatarUrl}
            answer={item.answer}
            answeredAt={item.answeredAt}
            reviewPlus={item.reviewPlus}
            reviewMinus={item.reviewMinus}
            title={item.title}
            images={item.images}
            isMobile={false}
            phrase={item.phrase}
            tags={item.tags}
          />
        ))}
        {hasReviewsSection &&
          items.length < 3 &&
          selectedFilters.length === 0 && (
            <NotEnoughReviews isEmptyReviews={false} />
          )}
        {isShowMoreReviews && (
          <div className={styles.showMoreContainer}>
            <Button
              functional
              className={styles.buttonShowMore}
              onClick={handleClick}
            >
              {isLoadingNew ? (
                <LoaderIcon className={styles.loader} />
              ) : (
                t('hotel.reviews.moreReviews')
              )}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
